import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { DataApiResponse } from '~/interfaces/api.interface';
import {
    ObjectDetectionSensitivity,
    SetBehaviorParams,
    SetHeatingAlgoParams,
    SetOffsetParams,
    TransferDescription,
    VisionRecognitionSensitivity,
} from '~/interfaces/device.interface';
import HttpService from '~/services/http.service';
import { ConfigModule } from '~/store/modules/config';
import { NotificationsModule } from '~common-store/modules/notifications';

import { MarketingsConsents } from '~/interfaces/user.interface';
import { Parameters as WeatherExtractCreateJobParams } from '~/interfaces/weather.interface';
import { handleError } from '~common-services/apiHelper';


async function post(
    endpoint: string,
    {
        params = null,
        apiUrl = ConfigModule.defaultApiUrl,
        textSuccess,
        textFailure,
        shouldHideError,
        config,
    }: {
        params: any,
        apiUrl?: string,
        textSuccess?: string,
        textFailure?: string,
        shouldHideError?: boolean,
        config?: AxiosRequestConfig,
    } = { params: null, apiUrl: ConfigModule.defaultApiUrl },
): Promise<AxiosResponse<any>> {
    //TODO replace when new route are up
    if (endpoint.startsWith('admin')) {
        endpoint = endpoint.replace('admin', '');
    }
    if (endpoint.includes('_')) {
        endpoint = endpoint.replaceAll('_', '');
    }
    try {
        const res = await HttpService.post(`${apiUrl}/${endpoint}`, params, config);
        if (textSuccess) {
            NotificationsModule.pushNotification({
                title: textSuccess,
                type: 'success',
            });
        }
        return res;
    } catch (error) {
        if (!shouldHideError) {
            handleError(error as DataApiResponse, textFailure);
        }
        throw(error);
    }
}

async function get(
    endpoint: string,
    {
        apiUrl = ConfigModule.defaultApiUrl,
        textSuccess,
        textFailure,
        shouldHideError,
        config,
    }: {
        apiUrl?: string,
        textSuccess?: string,
        textFailure?: string,
        shouldHideError?: boolean,
        config?: AxiosRequestConfig,
    } = { apiUrl: ConfigModule.defaultApiUrl },
): Promise<AxiosResponse<any>> {
    try {
        const res = await HttpService.get(`${apiUrl}/${endpoint}`, config);
        if (textSuccess) {
            NotificationsModule.pushNotification({
                title: textSuccess,
                type: 'success',
            });
        }
        return res;
    } catch (error) {
        if (!shouldHideError) {
            handleError(error as DataApiResponse, textFailure);
        }
        throw(error);
    }
}

async function getvpndeviceconfigs(params: { [key: string]: string }) {
    return await post('getvpndeviceconfigs', { params });
}

async function setvpndeviceconfigs(params: { config: { object_detection_sensitivity?: ObjectDetectionSensitivity, vision_recognition_sensitivity?: VisionRecognitionSensitivity }, device_id: string }) {
    return await post('setvpndeviceconfigs', { params });
}

async function getsupportdata(params: { [key: string]: string }) {
    return await post('getsupportdata', { params });
}

async function getusersinfo(params: { user_ids: string[] } | { user_mails: string[] }) {
    return await post('getusersinfo', { params });
}

async function geteliotdata(params: { [key: string]: string }) {
    return await post('geteliotdata', { params });
}

async function admingetadminrights() {
    return await get('users/me/privileges?app=support', { apiUrl: ConfigModule.apiAdminUsersUrl });
}

async function getsupportmacrangecsv() {
    return await post('getsupportmacrangecsv', { params: {}, shouldHideError: true });
}

async function gethomeowners(params: { home_id: string }) {
    return await post('gethomeowners', { params, shouldHideError: true });
}

async function getdeviceowners(params: { device_id: string }) {
    return await post('getdeviceowners', { params, shouldHideError: true });
}

async function getlogs(params: { [key: string]: string | undefined }) {
    return await post('search', {
        params: { ...params, app: 'support' },
        apiUrl: `${ConfigModule.apiAdminUsersUrl}/topology-log`,
    });
}

async function admingetorder(params: { order_id: string }) {
    return await get(`order/${params.order_id}`, { apiUrl: `${ConfigModule.apiAdminUsersUrl}/orders-history` });
}

async function admingetordersap(params: { sap_id: string }) {
    return await get(`sap/${params.sap_id}`, { apiUrl: `${ConfigModule.apiAdminUsersUrl}/orders-history` });
}

async function admingetinvoice(params: { order_id: string }) {
    return await get(`invoice/${params.order_id}`, { apiUrl: `${ConfigModule.apiAdminUsersUrl}/orders-history` });
}

async function admingetuserorders(params: { user_id: string }) {
    return await get(`user/${params.user_id}`, { apiUrl: `${ConfigModule.apiAdminUsersUrl}/orders-history` });
}

async function admindeleteaccount(params: { user_id: string }, textSuccess?: string) {
    return await post('admindeleteaccount', { params, textSuccess });
}

async function deleteidentitykeys(params: { user_id: string }, textSuccess?: string) {
    return await post('deleteidentitykeys', { params, textSuccess });
}

async function deleteemptyhomes(params: { user_id: string }, textSuccess?: string) {
    return await post('deleteemptyhomes', { params, textSuccess });
}

async function deleteeliotuser(params: { user_mail: string }, textSuccess?: string) {
    return await post('deleteeliotuser', { params, textSuccess });
}


async function admingetuserlogs(params: { user_id: string }) {
    return await post('admingetuserlogs', { params });
}

async function setBehavior(params: SetBehaviorParams, textSuccess?: string) {
    return await post('adminmodifydevicebehavior', { params, textSuccess });
}

async function setOffset(params: SetOffsetParams, textSuccess?: string) {
    return await post('adminsetdataoffset', { params, textSuccess });
}

async function setHeatingAlgo(params: SetHeatingAlgoParams, textSuccess?: string) {
    return await post('adminchangeheatingalgo', { params, textSuccess });
}

async function adminmodifygroup(params: { device_id: string, new_group_id: string }, textSuccess?: string) {
    return await post('adminmodifygroup', { params, textSuccess });
}

async function admingetplantinfo(params: { plant_id: string }) {
    return await post('admingetplantinfo', { params });
}

async function admingeteliotmodule(params: { search: string }) {
    return await post('admingeteliotmodule', { params });
}

async function admingetplantmodules(params: { plant_id: string }) {
    return await post('admingetplantmodules', { params });
}

async function getConfigs(params: { device_id: string }) {
    return await post('getConfigs', { params, apiUrl: ConfigModule.apiAdminSyncUrl, shouldHideError: true });
}

const getUserMarketingsConsents = async (params: { user_id: string }) => {
    return await post('apimarketing', { params, apiUrl: ConfigModule.apiAdminUsersUrl });
};

const unsubscribeUserMarketingsConsents = async (params: { user_id: string, consents: MarketingsConsents }) => {
    return await post('apimarketing/unsubscribe', { params, apiUrl: ConfigModule.apiAdminUsersUrl });
};

async function getUserConsent(params: { user_id: string }) {
    return await post('userconsentsmanagement_mkt_searchhistory', { params, shouldHideError: true });
}

async function removeUserFromHome(params: { home_id: string, user_id: string }, textSuccess?: string) {
    return await post('adminremoveuserfromhome', { params, textSuccess });
}

async function admingetdevicedump(params: { device_id: string }) {
    return await post('admingetdevicedump', { params });
}

async function admingetdevicedumplogs(params: { device_id: string }) {
    return await post('admingetdevicedumplogs', { params });
}

async function admingetdevicelogs(params: { device_id: string }) {
    return await post('admingetdevicelogs', { params });
}

async function recalibrateCO2(params: { device_id: string }) {
    const textSuccess = 'CO2 recalibration has been successfully asked';
    return await post('adminrecalibrateco2', { params, textSuccess });
}

async function adminenablessh(params: { device_id: string }) {
    return await post('adminenablessh', { params });
}

async function adminaskplugtoreconnect(params: { device_id: string }) {
    const textSuccess = 'Device will restart its main loop soon';
    return await post('adminaskplugtoreconnect', { params, textSuccess });
}

async function resetmigrationflag(params: { device_id: string }) {
    const textSuccess = 'Migration flag reset';
    return await post('resetmigrationflag', { params, textSuccess });
}

async function resetmigrationsecret(params: { device_id: string }) {
    const textSuccess = 'Migration secret reset';
    return await post('resetmigrationsecret', { params, textSuccess });
}

async function resetflag(params: { device_id: string }) {
    const textSuccess = 'Flag reset';
    return await post('resetflag', { params, textSuccess });
}

async function getOEMInfos(params: { device_id: string }) {
    const textSuccess = 'Get OEM info successfully performed, please reload your page after few minutes';
    return await post('getoeminfo', { params, apiUrl: ConfigModule.apiAdminSyncUrl, textSuccess });
}

async function getdebugconfigs(params: { device_id: string }) {
    return await post('getdebugconfigs', { params, apiUrl: ConfigModule.apiAdminSyncUrl });
}

async function setdebugconfigs(params: { device_id: string, options: Record<any, unknown> }, textSuccess?: string) {
    return await post('setdebugconfigs', {
        params: {
            device_id: params.device_id,
            ...params.options,
        },
        apiUrl: ConfigModule.apiAdminSyncUrl,
        textSuccess,
    });
}

async function getdiagnosis(params: { device_id: string, option: string }) {
    return await post('getdiagnosis', { params, apiUrl: ConfigModule.apiAdminSyncUrl });
}

async function adminupdatedevice(params: { device_id: string, debug_enabled?: boolean }, textSuccess?: string) {
    return await post('adminupdatedevice', { params, textSuccess });
}

async function adminresetdevice(params: { device_id: string, module_id?: string }) {
    return await post('adminresetdevice', { params });
}

async function adminimportmigrationdata(params: {
    device_id: string,
    type: string,
    user_mail: string,
    module_id?: string,
}) {
    return await post('adminimportmigrationdata', { params });
}

async function adminimportmigrationdata2(params: {
    user_id: string,
    target_bridge_id: string,
}) {
    return await post('adminimportmigrationdata2', { params });
}

async function adminmigratedata(params: {
    old_device_id: string,
    new_device_id: string,
    old_module_id: string | null,
    new_module_id: string | null,
    type: string,
    user_mail: string,
}) {
    return await post('adminmigratedata', { params });
}

async function adminmigratedata2(params: {
    transfert_description: TransferDescription,
    user_id: string,
}) {
    return await post('adminmigratedata2', { params });
}

async function admingetcameraevents(params: { camera_id: string }) {
    return await post('admingetcameraevents', { params });
}

async function admindeletedevicecache(params: { device_id: string }) {
    const textSuccess = 'Delete cache successfully performed';
    return await post('admindeletedevicecache', { params, textSuccess });
}

async function admindissociatemodule(params: { device_id: string, module_id: string }) {
    return await post('admindissociatemodule', { params });
}

async function adminsendvpncommand(
    params: { camera_id: string, command: string, verbose?: boolean },
    textSuccess?: string,
    textFailure?: string,
) {
    return await post('adminsendvpncommand', { params, textSuccess, textFailure });
}

async function dumpexternalmemory(params: { device_id: string }) {
    return await post('dumpexternalmemory', { params, apiUrl: ConfigModule.apiAdminSyncUrl });
}

async function adminorderdevicereboot(params: { device_id: string }) {
    const textSuccess = 'Order reboot successfully performed';
    return await post('adminorderdevicereboot', { params, textSuccess });
}

async function adminrecomputeheatingalgo(params: { device_id: string }) {
    const textSuccess = 'Device will recompute its heating algos soon';
    return await post('adminrecomputeheatingalgo', { params, textSuccess });
}

async function admintoggledevicedump(params: { device_id: string }) {
    const textSuccess = 'Toggle device dump successfully performed';
    return await post('admintoggledevicedump', { params, textSuccess });
}

async function admingenerateqrcode(params: { camera_id: string, cmd: string }, textSuccess: string) {
    return await post('admingenerateqrcode', { params, textSuccess, config: { responseType: 'blob' } });
}

async function switchDatabase(params: { device_id: string, target: string }) {
    const textSuccess = 'Successfully switched database';
    return await post('changenv2target', { params, apiUrl: ConfigModule.apiAdminSyncUrl, textSuccess });
}

async function unlocknetwork(params: { device_id: string }) {
    const textSuccess = 'Network unlock has been completed';
    return await post('unlocknetwork', { params, textSuccess });
}

async function movedevicetonewtemporaryhome(params: { device_id: string }) {
    const textSuccess = 'Device successfully moved to a temporary home';
    return await post('movedevicetonewtemporaryhome', { params, textSuccess });
}

async function admingetdevicegroups(params: { type: string }, textFailure?: string) {
    return await post('admingetdevicegroups', { params, textFailure });
}

async function admindeleteplant(params: { plant_id: string }) {
    const textSuccess = 'Plant successfully deleted';
    return await post('admindeleteplant', { params, textSuccess });
}

async function getdevicesecret(params: { device_id: string }) {
    return await post('getdevicesecret', { params });
}

async function rollbackmigration(params: { device_id: string, migrated: boolean }) {
    return await post('setmigration', { params, apiUrl: ConfigModule.apiAdminSyncUrl });
}

async function getlocalresources(params: { home_id: string, device_id: string }) {
    return await post('getlocalresources', { params, apiUrl: ConfigModule.apiAdminSyncUrl });
}


async function associateuser(params: { device_id: string, mail: string }) {
    return await post('associateuser', { params });
}

async function getWeatherExtractJobs() {
    return await get('getweatherextractjobs', { apiUrl: ConfigModule.weatherExtractApiUrl });
}

async function createWeatherExtractJob(ExportParameters: WeatherExtractCreateJobParams) {
    return await post('createweatherextractjob', {
        params: { 'export_parameters': ExportParameters },
        apiUrl: ConfigModule.weatherExtractApiUrl,
    });
}

async function cancelWeatherExtractJob(params: { job_id: string }) {
    return await post('cancelweatherextractjob', { params, apiUrl: ConfigModule.weatherExtractApiUrl });
}

async function startweatherextractjob(params: { job_id: string }) {
    return await post('startweatherextractjob', { params, apiUrl: ConfigModule.weatherExtractApiUrl });
}

async function startweatherquotejob(params: { job_id: string }) {
    return await post('startweatherquotejob', { params, apiUrl: ConfigModule.weatherExtractApiUrl });
}

async function deleteweatherjob(params: { job_id: string }) {
    return await post('deleteweatherjob', { params, apiUrl: ConfigModule.weatherExtractApiUrl });
}

async function getavailableflags() {
    return await get('getavailableflags', { apiUrl: ConfigModule.userFlagsApiUrl });
}

async function getuserflags(params: { user_id: string }) {
    return await post('getuserflags', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function adduserflag(params: { user_id: string, flag_id: string }) {
    return await post('adduserflag', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}


async function removeuserflag(params: { user_id: string, flag_id: string }) {
    return await post('removeuserflag', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function createflag(params: { name: string, description: string, expire_at: number }) {
    return await post('createflag', { params: { flag: params }, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function updateflag(params: { flag_id: string, description?: string, expire_at?: number, released?: boolean }) {
    return await post('updateflag', { params: { flag: params }, apiUrl: ConfigModule.userFlagsApiUrl });
}

const USE_MOCK = false;

async function releaseflag(params: { flag_id: string, progressive: number }) {
    if (!USE_MOCK) {
        return await post('releaseflag', { params: { flag: params }, apiUrl: ConfigModule.userFlagsApiUrl });
    }
    return {
        data: {
            body: {}
        }
    };
}

async function revertflag(params: { flag_id: string }) {
    if (!USE_MOCK) {
        return await post('revertflag', { params: { flag: params }, apiUrl: ConfigModule.userFlagsApiUrl });
    }
    return {
        data: {
            body: {}
        }
    };
}

async function deleteflag(params: { flag_id: string }) {
    return await post('deleteflag', { params: { flag: params }, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function getavailablegroups() {
    return await get('getavailablegroups', { apiUrl: ConfigModule.userFlagsApiUrl });
}

async function creategroup(params: { name: string }) {
    return await post('creategroup', { params: { group: params }, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function updategroup(params: { group_id: string, name: string }) {
    return await post('updategroup', { params: { group: params }, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function deletegroup(params: { group_id: string }) {
    return await post('deletegroup', { params: { group: params }, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function addgroupuser(params: { group_id: string, user_id: string }) {
    return await post('addgroupuser', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function removegroupuser(params: { group_id: string, user_id: string }) {
    return await post('removegroupuser', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function addgroupflag(params: { group_id: string, flag_id: string }) {
    return await post('addgroupflag', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}

async function removegroupflag(params: { group_id: string, flag_id: string }) {
    return await post('removegroupflag', { params, apiUrl: ConfigModule.userFlagsApiUrl });
}


export default {

    getvpndeviceconfigs: (params: { [key: string]: string }) => getvpndeviceconfigs(params),
    setvpndeviceconfigs: (params: { config: { object_detection_sensitivity?: ObjectDetectionSensitivity, vision_recognition_sensitivity?: VisionRecognitionSensitivity }, device_id: string }) => setvpndeviceconfigs(params),

    getWeatherExtractJobs: () => getWeatherExtractJobs(),
    createWeatherExtractJob: (params: WeatherExtractCreateJobParams) => createWeatherExtractJob(params),
    cancelWeatherExtractJob: (params: { job_id: string }) => cancelWeatherExtractJob(params),
    startweatherextractjob: (params: { job_id: string }) => startweatherextractjob(params),
    startweatherquotejob: (params: { job_id: string }) => startweatherquotejob(params),
    deleteWeatherExtractJob: (params: { job_id: string }) => deleteweatherjob(params),

    getsupportdata: (params: { [key: string]: string }) => getsupportdata(params),
    getusersinfo: (params: { user_ids: string[] } | { user_mails: string[] }) => getusersinfo(params),
    geteliotdata: (params: { [key: string]: string }) => geteliotdata(params),
    admingetadminrights: () => admingetadminrights(),
    getsupportmacrangecsv: () => getsupportmacrangecsv(),
    gethomeowners: (params: { home_id: string }) => gethomeowners(params),
    getdeviceowners: (params: { device_id: string }) => getdeviceowners(params),
    getlogs: (params: { [key: string]: string | undefined }) => getlogs(params),
    admingetorder: (params: { order_id: string }) => admingetorder(params),
    admingetordersap: (params: { sap_id: string }) => admingetordersap(params),
    admingetinvoice: (params: { order_id: string }) => admingetinvoice(params),
    admingetuserorders: (params: { user_id: string }) => admingetuserorders(params),
    admindeleteaccount: (params: { user_id: string }, textSuccess?: string) => admindeleteaccount(params, textSuccess),
    deleteidentitykeys: (params: { user_id: string }, textSuccess?: string) => deleteidentitykeys(params, textSuccess),
    deleteemptyhomes: (params: { user_id: string }, textSuccess?: string) => deleteemptyhomes(params, textSuccess),
    deleteeliotuser: (params: { user_mail: string }, textSuccess?: string) => deleteeliotuser(params, textSuccess),
    admingetuserlogs: (params: { user_id: string }) => admingetuserlogs(params),
    setBehavior: (params: SetBehaviorParams, textSuccess?: string) => setBehavior(params, textSuccess),
    setOffset: (params: SetOffsetParams, textSuccess?: string) => setOffset(params, textSuccess),
    setHeatingAlgo: (params: SetHeatingAlgoParams, textSuccess?: string) => setHeatingAlgo(params, textSuccess),
    adminmodifygroup: (
        params: { device_id: string, new_group_id: string },
        textSuccess?: string,
    ) => adminmodifygroup(params, textSuccess),
    admingetplantinfo: (params: { plant_id: string }) => admingetplantinfo(params),
    admingeteliotmodule: (params: { search: string }) => admingeteliotmodule(params),
    admingetplantmodules: (params: { plant_id: string }) => admingetplantmodules(params),
    getConfigs: (params: { device_id: string }) => getConfigs(params),
    getUserConsent: (params: { user_id: string }) => getUserConsent(params),
    getUserMarketingsConsents: (params: { user_id: string }) => getUserMarketingsConsents(params),
    unsubscribeUserMarketingsConsents: (params: { user_id: string, consents: MarketingsConsents }) => unsubscribeUserMarketingsConsents(params),
    removeUserFromHome: (
        params: { home_id: string, user_id: string },
        textSuccess?: string,
    ) => removeUserFromHome(params, textSuccess),
    admingetdevicedump: (params: { device_id: string }) => admingetdevicedump(params),
    admingetdevicedumplogs: (params: { device_id: string }) => admingetdevicedumplogs(params),
    admingetdevicelogs: (params: { device_id: string }) => admingetdevicelogs(params),
    recalibrateCO2: (params: { device_id: string }) => recalibrateCO2(params),
    adminenablessh: (params: { device_id: string }) => adminenablessh(params),
    adminaskplugtoreconnect: (params: { device_id: string }) => adminaskplugtoreconnect(params),
    resetmigrationflag: (params: { device_id: string }) => resetmigrationflag(params),
    resetmigrationsecret: (params: { device_id: string }) => resetmigrationsecret(params),
    resetflag: (params: { device_id: string }) => resetflag(params),
    getOEMInfos: (params: { device_id: string }) => getOEMInfos(params),
    getdebugconfigs: (params: { device_id: string }) => getdebugconfigs(params),
    setdebugconfigs: (params: { device_id: string, options: Record<any, unknown> }, textSuccess?: string) => setdebugconfigs(params, textSuccess),
    getdiagnosis: (params: { device_id: string, option: string }) => getdiagnosis(params),
    adminupdatedevice: (
        params: { device_id: string, debug_enabled?: boolean },
        textSuccess?: string,
    ) => adminupdatedevice(params, textSuccess),
    adminresetdevice: (params: { device_id: string, module_id?: string }) => adminresetdevice(params),
    adminimportmigrationdata: (params: {
        device_id: string,
        type: string,
        user_mail: string,
        module_id?: string,
    }) => adminimportmigrationdata(params),
    adminimportmigrationdata2: (params: {
        user_id: string,
        target_bridge_id: string,
    }) => adminimportmigrationdata2(params),
    adminmigratedata: (params: {
        old_device_id: string,
        new_device_id: string,
        old_module_id: string | null,
        new_module_id: string | null,
        type: string,
        user_mail: string,
    }) => adminmigratedata(params),
    adminmigratedata2: (params: {
        transfert_description: TransferDescription,
        user_id: string,
    }) => adminmigratedata2(params),
    admingetcameraevents: (params: { camera_id: string }) => admingetcameraevents(params),
    admindeletedevicecache: (params: { device_id: string }) => admindeletedevicecache(params),
    admindissociatemodule: (params: { device_id: string, module_id: string }) => admindissociatemodule(params),
    adminsendvpncommand: (
        params: { camera_id: string, command: string, verbose?: boolean },
        textSuccess?: string,
        textFailure?: string,
    ) => adminsendvpncommand(params, textSuccess, textFailure),
    dumpexternalmemory: (params: { device_id: string }) => dumpexternalmemory(params),
    adminorderdevicereboot: (params: { device_id: string }) => adminorderdevicereboot(params),
    adminrecomputeheatingalgo: (params: { device_id: string }) => adminrecomputeheatingalgo(params),
    admintoggledevicedump: (params: { device_id: string }) => admintoggledevicedump(params),
    admingenerateqrcode: (
        params: { camera_id: string, cmd: string },
        textSuccess: string,
    ) => admingenerateqrcode(params, textSuccess),
    switchDatabase: (params: { device_id: string, target: string }) => switchDatabase(params),
    unlocknetwork: (params: { device_id: string }) => unlocknetwork(params),
    movedevicetonewtemporaryhome: (params: { device_id: string }) => movedevicetonewtemporaryhome(params),
    admingetdevicegroups: (params: { type: string }, textFailure?: string) => admingetdevicegroups(params, textFailure),
    admindeleteplant: (params: { plant_id: string }) => admindeleteplant(params),
    getdevicesecret: (params: { device_id: string }) => getdevicesecret(params),
    rollbackmigration: (params: { device_id: string, migrated: boolean }) => rollbackmigration(params),
    getlocalresources: (params: { home_id: string, device_id: string }) => getlocalresources(params),
    associateuser: (params: { device_id: string, mail: string }) => associateuser(params),
    getavailableflags: () => getavailableflags(),
    getuserflags: (params: { user_id: string }) => getuserflags(params),
    adduserflag: (params: { user_id: string, flag_id: string }) => adduserflag(params),
    removeuserflag: (params: { user_id: string, flag_id: string }) => removeuserflag(params),
    createflag: (params: { name: string, description: string, expire_at: number }) => createflag(params),
    updateflag: (params: { flag_id: string, description?: string, expire_at?: number, released?: boolean }) => updateflag(params),
    releaseflag: (params: { flag_id: string, progressive: number }) => releaseflag(params),
    revertflag: (params: { flag_id: string }) => revertflag(params),
    deleteflag: (params: { flag_id: string }) => deleteflag(params),
    getavailablegroups: () => getavailablegroups(),
    creategroup: (params: { name: string }) => creategroup(params),
    updategroup: (params: { group_id: string, name: string }) => updategroup(params),
    deletegroup: (params: { group_id: string }) => deletegroup(params),
    addgroupuser: (params: { group_id: string, user_id: string }) => addgroupuser(params),
    removegroupuser: (params: { group_id: string, user_id: string }) => removegroupuser(params),
    addgroupflag: (params: { group_id: string, flag_id: string }) => addgroupflag(params),
    removegroupflag: (params: { group_id: string, flag_id: string }) => removegroupflag(params),
};
